import React from 'react'

import type { LucideIcon } from 'lucide-react'
import { HiMiniCheck } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { RadioGroup, RadioGroupCardItem } from '@/components/form/Radio'

export interface WizardOption {
  id: string
  label: string
  description: string
  icon: LucideIcon
  tileClassName: string
  /** Card chrome when selected; defaults to the accent treatment. */
  selectedClassName?: string
  /** Selection-circle fill when selected; defaults to accent. */
  selectedIndicatorClassName?: string
}

interface OptionItemProps {
  option: WizardOption
  isSelected: boolean
}

const OptionRow: React.FC<OptionItemProps> = ({ option, isSelected }) => {
  const Icon = option.icon

  return (
    <RadioGroupCardItem
      value={option.id}
      className={twMerge(
        'flex items-center gap-x-3 w-full p-2 rounded-lg text-left border transition-colors outline-none',
        'focus-visible:ring-2 focus-visible:ring-accent-400/40',
        isSelected
          ? 'border-accent-400/70 dark:border-accent-500/50 bg-accent-50/60 dark:bg-accent-500/10'
          : 'border-transparent hover:bg-primary-50 dark:hover:bg-primary-800/60',
      )}
    >
      <div
        className={twMerge(
          'flex flex-shrink-0 items-center justify-center w-10 h-10 rounded-lg',
          option.tileClassName,
        )}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] leading-5 font-semibold text-primary-900 dark:text-primary-100">
          {option.label}
        </div>
        <p className="mt-0.5 text-sm text-primary-600 dark:text-primary-400">
          {option.description}
        </p>
      </div>
      <div
        aria-hidden="true"
        className={twMerge(
          'flex flex-shrink-0 items-center justify-center w-5 h-5 rounded-full bg-accent-500 transition-opacity',
          isSelected ? 'opacity-100' : 'opacity-0',
        )}
      >
        <HiMiniCheck className="w-3.5 h-3.5 text-white" />
      </div>
    </RadioGroupCardItem>
  )
}

/**
 * Card variant for horizontal (side-by-side) layouts: a tall vertical card
 * with a large borderless icon tile on top and a selection circle pinned to
 * the corner, so each option reads as a destination rather than a list row.
 */
const OptionCard: React.FC<OptionItemProps> = ({ option, isSelected }) => {
  const Icon = option.icon

  return (
    <RadioGroupCardItem
      value={option.id}
      className={twMerge(
        // Top-aligned (not centered) so icon/title/description line up across
        // sibling cards regardless of how long each description runs.
        'group relative flex h-full min-h-[200px] w-full flex-col gap-4 rounded-xl border p-6 text-left outline-none',
        'transition-[border-color,background-color,box-shadow,transform] duration-150 motion-safe:active:scale-[0.98]',
        'focus-visible:ring-2 focus-visible:ring-accent-400/40',
        isSelected
          ? (option.selectedClassName ??
              'border-accent-400 dark:border-accent-500/60 bg-accent-50/70 dark:bg-accent-500/10 shadow-sm ring-1 ring-accent-400/25 dark:ring-accent-500/20')
          : 'border-primary-200 dark:border-primary-700 bg-white dark:bg-default-dark hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-sm',
      )}
    >
      <div
        aria-hidden="true"
        className={twMerge(
          'absolute right-4 top-4 flex items-center justify-center w-5 h-5 rounded-full transition-colors duration-150',
          isSelected
            ? (option.selectedIndicatorClassName ?? 'bg-accent-500')
            : 'border-[1.5px] border-primary-300 dark:border-primary-600 group-hover:border-primary-400 dark:group-hover:border-primary-500',
        )}
      >
        <HiMiniCheck
          className={twMerge(
            'w-3.5 h-3.5 text-white transition-[opacity,transform] duration-150 ease-[cubic-bezier(0.2,0,0,1)]',
            isSelected ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.25]',
          )}
        />
      </div>
      <div
        className={twMerge(
          // rounded-xl matches the card's radius — a larger inner radius than
          // the outer surface reads as off.
          'flex flex-shrink-0 items-center justify-center w-14 h-14 rounded-xl',
          'transition-transform duration-150 motion-safe:group-hover:scale-105',
          option.tileClassName,
        )}
      >
        <Icon className="w-7 h-7" />
      </div>
      <div>
        <div className="text-[15px] leading-5 font-semibold text-primary-900 dark:text-primary-100">
          {option.label}
        </div>
        <p className="mt-1.5 text-sm leading-5 text-pretty text-primary-600 dark:text-primary-400">
          {option.description}
        </p>
      </div>
    </RadioGroupCardItem>
  )
}

interface OptionsStepProps {
  /** Omit when the surrounding wizard renders its own step heading. */
  heading?: string
  subheading?: string
  ariaLabel: string
  options: WizardOption[]
  selectedId: string
  onSelect: (id: string) => void
  /**
   * 'vertical' (default) stacks compact rows; 'horizontal' lays options out
   * side by side as bordered cards.
   */
  orientation?: 'vertical' | 'horizontal'
}

export const OptionsStep: React.FC<OptionsStepProps> = ({
  heading,
  subheading,
  ariaLabel,
  options,
  selectedId,
  onSelect,
  orientation = 'vertical',
}) => {
  const OptionItem = orientation === 'horizontal' ? OptionCard : OptionRow

  const radioGroup = (
    <RadioGroup
      aria-label={ariaLabel}
      value={selectedId}
      onValueChange={onSelect}
      className={
        orientation === 'horizontal'
          ? 'grid-cols-1 sm:grid-cols-2 gap-4'
          : 'gap-1.5'
      }
    >
      {options.map((option) => (
        <OptionItem
          key={option.id}
          option={option}
          isSelected={option.id === selectedId}
        />
      ))}
    </RadioGroup>
  )

  if (!heading) {
    return radioGroup
  }

  return (
    <div className="space-y-4 px-1 pt-2 pb-1">
      <div className="space-y-1">
        <h3 className="text-base font-semibold text-primary-900 dark:text-primary-100">
          {heading}
        </h3>
        {subheading ? (
          <p className="text-sm text-primary-600 dark:text-primary-400">
            {subheading}
          </p>
        ) : null}
      </div>
      {radioGroup}
    </div>
  )
}

OptionsStep.displayName = 'Wizard.OptionsStep'
